import styled from 'styled-components';
import { Droppable, Draggable } from 'react-beautiful-dnd';

import { ASCENDING, DESCENDING, SortDirections } from 'constants/sortDirections';
import { spacing, colors } from 'ui/theme';
import { ColumnData } from './constants';

import TableHeaderCell from '../tableHeaderCell/TableHeaderCell';

const StyledHeader = styled.div`
  background: ${colors.white};
  border-color: ${colors.grey15};
  border-style: solid;
  border-width: 0;
  ${({ isSticky }: { isSticky: boolean }) => !isSticky && `border-bottom-width: ${spacing.single};`}
  display: table-row;
  width: 100%;
`;

export type DraggableHeaderProps<SortType extends string = string> = {
  columns: ColumnData[];
  setSortedBy: (sortedBy: SortType) => void;
  setSortDirection: (sortDirection: string) => void;
  sortedBy: SortType;
  sortDirection: SortDirections;
  isSticky?: boolean;
  isStaticFirstCol?: boolean;
  HeaderCell?: any;
};

export function DraggableHeader<SortType extends string = string>({
  columns,
  setSortedBy,
  setSortDirection,
  sortedBy,
  sortDirection,
  isSticky = false,
  isStaticFirstCol = false,
  HeaderCell = TableHeaderCell,
  ...props
}: DraggableHeaderProps<SortType>) {
  if (!columns.length) {
    return null;
  }

  return (
    <Droppable droppableId="droppable" direction="horizontal">
      {(droppableProvided) => (
        <StyledHeader
          data-test="table-header-row"
          role="row"
          isSticky={isSticky}
          ref={droppableProvided.innerRef}
          {...props}
        >
          {columns.map((column, index) => {
            const id = column.value;

            const isActiveSort = sortedBy === id; // if this column is the active sorting index
            if (isStaticFirstCol && index === 0) {
              return (
                <HeaderCell
                  isActiveSort={isActiveSort}
                  columnData={column}
                  key={id}
                  handleClick={() => {
                    if (isActiveSort) {
                      const newSortDirection = sortDirection === ASCENDING ? DESCENDING : ASCENDING;
                      setSortDirection(newSortDirection);
                    } else {
                      setSortedBy(id as unknown as SortType);
                    }
                  }}
                  sortable={column.sortable}
                  sortDirection={sortDirection}
                  isSticky={isSticky}
                  align={column?.align}
                />
              );
            }
            return (
              <Draggable key={id} draggableId={id} index={index}>
                {(provided) => {
                  return (
                    <HeaderCell
                      isActiveSort={isActiveSort}
                      columnData={column}
                      key={id}
                      handleClick={() => {
                        if (isActiveSort) {
                          const newSortDirection =
                            sortDirection === ASCENDING ? DESCENDING : ASCENDING;
                          setSortDirection(newSortDirection);
                        } else {
                          setSortedBy(id as unknown as SortType);
                        }
                      }}
                      sortable={column.sortable}
                      sortDirection={sortDirection}
                      isSticky={isSticky}
                      ref={provided.innerRef}
                      align={column?.align}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                    />
                  );
                }}
              </Draggable>
            );
          })}
          {droppableProvided.placeholder}
        </StyledHeader>
      )}
    </Droppable>
  );
}

export default DraggableHeader;
